import React, { Component, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { signInWithRedirect, signOut, getCurrentUser } from 'aws-amplify/auth';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import AppLayout from '@cloudscape-design/components/app-layout';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import Flashbar from '@cloudscape-design/components/flashbar';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import Box from '@cloudscape-design/components/box';
import Modal from '@cloudscape-design/components/modal';
import { NotificationsProvider, useNotifications } from './hooks/useNotifications';
import { api, getStage, SessionExpiredError } from './api/client';
import { ApplicationsPage } from './pages/ApplicationsPage';
import { UsersPage } from './pages/UsersPage';
import { TeamsPage } from './pages/TeamsPage';
import { EntityPage } from './pages/EntityPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { UserUserGroupPage } from './pages/UserUserGroupPage';
import { UserResourceActionPage } from './pages/UserResourceActionPage';
import { UserGroupResourceActionPage } from './pages/UserGroupResourceActionPage';
import { UserRoleResourcePage } from './pages/UserRoleResourcePage';
import { RuntimePage } from './pages/RuntimePage';
import { LockPage } from './pages/LockPage';

class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: string | null }> {
  state = { error: null as string | null };
  static getDerivedStateFromError(e: Error) { return { error: e.message }; }
  render() {
    if (this.state.error) return (
      <Flashbar items={[{
        type: 'error',
        header: 'Something went wrong',
        content: this.state.error,
        action: <Button onClick={() => { this.setState({ error: null }); location.reload(); }}>Reload</Button>,
      }]} />
    );
    return this.props.children;
  }
}

function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const stage = getStage();

  return (
    <SideNavigation
      header={{
        text: `Keystone IAM [${stage.toUpperCase()}]`,
        href: '/',
      }}
      activeHref={location.pathname}
      onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
      items={[
        {
          type: 'section',
          text: 'Entities',
          items: [
            { type: 'link', text: 'Applications', href: '/' },
            { type: 'link', text: 'Users', href: '/users' },
            { type: 'link', text: 'Teams', href: '/teams' },
            { type: 'link', text: 'User Groups', href: '/user-groups' },
            { type: 'link', text: 'Roles', href: '/roles' },
            { type: 'link', text: 'Actions', href: '/actions' },
            { type: 'link', text: 'Resources', href: '/resources' },
          ],
        },
        {
          type: 'section',
          text: 'Policies',
          items: [
            { type: 'link', text: 'User - UserGroup', href: '/policy/user-user-group' },
            { type: 'link', text: 'User - Resource - Action', href: '/policy/user-resource-action' },
            { type: 'link', text: 'Group - Resource - Action', href: '/policy/user-group-resource-action' },
            { type: 'link', text: 'User - Role - Resource', href: '/policy/user-role-resource' },
          ],
        },
        {
          type: 'section',
          text: 'Runtime',
          items: [
            { type: 'link', text: 'Query Permissions', href: '/runtime' },
          ],
        },
        {
          type: 'section',
          text: 'Lock',
          items: [
            { type: 'link', text: 'Lock Management', href: '/lock' },
          ],
        },
      ]}
    />
  );
}

function AppContent() {
  const [app, setApp] = useState(localStorage.getItem('iam_app') || '');
  const [apps, setApps] = useState<{ name: string }[]>([]);
  const [user, setUser] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('iam_theme') === 'dark');
  const [sessionExpired, setSessionExpired] = useState(false);
  const { items: notifications, addNotification } = useNotifications();

  useEffect(() => {
    applyMode(darkMode ? Mode.Dark : Mode.Light);
    localStorage.setItem('iam_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    getCurrentUser()
      .then(u => {
        setUser(u.signInDetails?.loginId ?? u.username);
        setAuthLoading(false);
      })
      .catch(() => {
        signInWithRedirect();
      });
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    api('GET', '/applications').then(d => {
      if (Array.isArray(d)) setApps(d);
    }).catch((err) => {
      if (err instanceof SessionExpiredError) {
        setSessionExpired(true);
      } else {
        addNotification('error', 'Failed to load applications');
      }
    });
  }, [authLoading, user]);

  const onAppChange = (a: string) => {
    setApp(a);
    localStorage.setItem('iam_app', a);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  if (authLoading) {
    return (
      <>
        <TopNavigation
          identity={{ href: '/', title: 'Keystone IAM Console' }}
          utilities={[]}
        />
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Spinner size="large" />
          <Box variant="p" padding={{ top: 's' }}>Authenticating...</Box>
        </Box>
      </>
    );
  }

  const stage = getStage();

  return (
    <>
      <TopNavigation
        identity={{ href: '/', title: 'Keystone IAM Console' }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: app || 'Select App',
            items: apps.map(a => ({ id: a.name, text: a.name })),
            onItemClick: ({ detail }) => onAppChange(detail.id),
          },
          {
            type: 'button',
            text: stage.toUpperCase(),
          },
          {
            type: 'button',
            iconName: darkMode ? 'status-positive' : 'status-stopped',
            text: darkMode ? 'Dark' : 'Light',
            onClick: () => setDarkMode(prev => !prev),
          },
          {
            type: 'menu-dropdown',
            text: user || '',
            iconName: 'user-profile',
            items: [
              { id: 'signout', text: 'Sign out' },
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === 'signout') handleSignOut();
            },
          },
        ]}
      />
      <AppLayout
        navigation={<Navigation />}
        toolsHide
        notifications={<Flashbar items={notifications} />}
        content={
          <Routes>
            <Route path="/" element={<ApplicationsPage apps={apps} onAppsChange={setApps} />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/user-groups" element={<EntityPage app={app} title="User Groups" endpoint="user_group" endpointPlural="user_groups" />} />
            <Route path="/roles" element={<EntityPage app={app} title="Roles" endpoint="role" endpointPlural="roles" />} />
            <Route path="/actions" element={<EntityPage app={app} title="Actions" endpoint="action" endpointPlural="actions" />} />
            <Route path="/resources" element={<ResourcesPage app={app} />} />
            <Route path="/policy/user-user-group" element={<UserUserGroupPage app={app} />} />
            <Route path="/policy/user-resource-action" element={<UserResourceActionPage app={app} />} />
            <Route path="/policy/user-group-resource-action" element={<UserGroupResourceActionPage app={app} />} />
            <Route path="/policy/user-role-resource" element={<UserRoleResourcePage app={app} />} />
            <Route path="/runtime" element={<RuntimePage app={app} />} />
            <Route path="/lock" element={<LockPage app={app} />} />
          </Routes>
        }
      />
      <Modal
        visible={sessionExpired}
        header="Session Expired"
        closeAriaLabel="Close"
        onDismiss={() => setSessionExpired(false)}
        footer={
          <Box float="right">
            <Button variant="primary" onClick={() => signInWithRedirect()}>
              Sign in again
            </Button>
          </Box>
        }
      >
        Your session has expired. Please sign in again to continue.
      </Modal>
    </>
  );
}

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <NotificationsProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </NotificationsProvider>
    </BrowserRouter>
  );
};

export default App;
